import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, Send, MessageSquare, Search, Megaphone, ChevronRight,
  Calendar as CalendarIcon, X
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Threaded per-camper message inbox.
 * Used by both Admin and Supervisor dashboards when a group is selected.
 *
 * Props:
 *   token       - JWT (required)
 *   groupCode   - e.g., "B12" (required)
 *   groupName   - display label (optional, falls back to groupCode)
 *   campers     - array of campers in this group (id, first_name, last_name, preferred_name, parent_info)
 *   onBack      - callback to return to the group picker
 */
export default function ThreadedMessagesInbox({ token, groupCode, groupName, campers = [], onBack }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeThread, setActiveThread] = useState(null); // camper_id | "compose-all" | null
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState(""); // YYYY-MM-DD; empty = all
  const [msgText, setMsgText] = useState("");
  const [channel, setChannel] = useState("sms");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    if (!groupCode) return;
    setLoading(true);
    try {
      const params = {};
      if (dateFilter) {
        params.date_from = dateFilter;
        params.date_to = dateFilter;
      }
      const res = await axios.get(`${API}/supervisor/messages/${groupCode}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setMessages(res.data || []);
    } catch (e) {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [groupCode, token, dateFilter]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    if (bottomRef.current && activeThread) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, activeThread]);

  const formatTime = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const opts = { timeZone: "America/New_York" };
    const isToday = d.toLocaleDateString("en-US", opts) === now.toLocaleDateString("en-US", opts);
    if (isToday) return d.toLocaleTimeString("en-US", { ...opts, hour: "numeric", minute: "2-digit" });
    return d.toLocaleDateString("en-US", { ...opts, month: "short", day: "numeric" }) + " " +
      d.toLocaleTimeString("en-US", { ...opts, hour: "numeric", minute: "2-digit" });
  };

  // Build per-camper conversation list from raw messages
  const buildConversations = () => {
    const convos = {};

    messages.filter(m => m.direction === "inbound" && m.camper_id).forEach(msg => {
      const cid = msg.camper_id;
      if (!convos[cid]) convos[cid] = { camper_id: cid, messages: [], lastTime: msg.timestamp };
      convos[cid].messages.push(msg);
      if (new Date(msg.timestamp) > new Date(convos[cid].lastTime)) convos[cid].lastTime = msg.timestamp;
    });

    messages.filter(m => m.direction === "outbound" && m.recipients).forEach(msg => {
      msg.recipients.forEach(r => {
        const cid = r.camper_id;
        if (!cid) return;
        if (!convos[cid]) convos[cid] = { camper_id: cid, messages: [], lastTime: msg.timestamp };
        if (!convos[cid].messages.find(m => m.id === msg.id)) convos[cid].messages.push(msg);
        if (new Date(msg.timestamp) > new Date(convos[cid].lastTime)) convos[cid].lastTime = msg.timestamp;
      });
    });

    return Object.values(convos).map(c => {
      const camper = campers.find(x => x.id === c.camper_id);
      const recipientName = (() => {
        for (const m of c.messages) {
          if (m.direction === "outbound" && Array.isArray(m.recipients)) {
            const r = m.recipients.find(rr => rr.camper_id === c.camper_id);
            if (r?.camper_name) return r.camper_name;
          }
        }
        return null;
      })();
      const camperName = camper
        ? `${camper.first_name} ${camper.last_name}`
        : (recipientName || "Unknown camper");
      const lastMessage = [...c.messages].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
      return {
        ...c,
        camperName,
        parentName: camper?.parent_info?.name || "",
        parentEmail: camper?.parent_info?.email || "",
        parentPhone: camper?.parent_info?.phone || "",
        lastMessage,
      };
    }).sort((a, b) => new Date(b.lastTime) - new Date(a.lastTime));
  };

  const conversations = buildConversations();

  const getThreadMessages = (camperId) => {
    const out = [];
    messages.forEach(msg => {
      if (msg.direction === "inbound" && msg.camper_id === camperId) {
        out.push(msg);
      } else if (msg.direction === "outbound" && Array.isArray(msg.recipients)) {
        if (msg.recipients.some(r => r.camper_id === camperId)) out.push(msg);
      }
    });
    return out.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  const activeCamper = activeThread && activeThread !== "compose-all"
    ? campers.find(c => c.id === activeThread)
    : null;
  const activeConvo = activeThread && activeThread !== "compose-all"
    ? getThreadMessages(activeThread)
    : [];

  const handleSend = async (camperIds) => {
    if (!msgText.trim()) return toast.error("Enter a message");
    setSending(true);
    try {
      const body = { message: msgText, channel, group_code: groupCode };
      if (camperIds && camperIds.length > 0) body.camper_ids = camperIds;
      const res = await axios.post(`${API}/messages/send`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Sent to ${res.data.recipient_count} parent(s)`);
      setMsgText("");
      setTimeout(fetchMessages, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e, camperIds) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(camperIds);
    }
  };

  const filteredConvos = conversations.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.camperName.toLowerCase().includes(q) || (c.parentName || "").toLowerCase().includes(q);
  });

  const campersWithoutConvo = campers.filter(c => !conversations.find(cv => cv.camper_id === c.id));
  const filteredNewCampers = campersWithoutConvo.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.first_name || "").toLowerCase().includes(q) || (c.last_name || "").toLowerCase().includes(q);
  });

  // ═══════════════════ THREAD VIEW ═══════════════════
  if (activeThread && activeThread !== "compose-all") {
    const threadLabel = activeCamper
      ? `${activeCamper.first_name} ${activeCamper.last_name}`
      : (conversations.find(c => c.camper_id === activeThread)?.camperName || "Conversation");
    const parentLabel = activeCamper?.parent_info?.name || activeCamper?.parent_info?.email || "";

    return (
      <div className="flex flex-col h-[calc(100vh-120px)] bg-gray-50 rounded-lg border" data-testid="threaded-inbox-thread">
        {/* Thread header */}
        <div className="bg-white border-b px-4 py-3 rounded-t-lg">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setActiveThread(null)} data-testid="thread-back-to-inbox">
              <ArrowLeft className="w-4 h-4 mr-1" />Back
            </Button>
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
              {threadLabel.split(" ").map(w => w[0]).join("").slice(0, 2)}
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-bold truncate" data-testid="thread-camper-name">{threadLabel}</h2>
              <p className="text-xs text-muted-foreground truncate">{parentLabel}</p>
            </div>
            <Badge variant="outline" className="text-[10px] ml-auto">{groupName || groupCode}</Badge>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4" style={{ WebkitOverflowScrolling: "touch" }}>
          {activeConvo.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-20" />
              <p className="text-sm">No messages yet — type below to start.</p>
            </div>
          ) : (
            <div className="space-y-3 max-w-3xl mx-auto">
              {activeConvo.map(msg => {
                const isOutbound = msg.direction === "outbound";
                return (
                  <div key={msg.id} className={`flex ${isOutbound ? "justify-end" : "justify-start"}`} data-testid={`thread-msg-${msg.id}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                      isOutbound
                        ? "bg-blue-600 text-white rounded-br-md"
                        : "bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm"
                    }`}>
                      {!isOutbound && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-xs">{msg.from_name || msg.from_phone || msg.from_email}</span>
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                            {msg.channel === "sms" ? "SMS" : "Email"}
                          </Badge>
                        </div>
                      )}
                      <p className={`text-sm whitespace-pre-wrap ${isOutbound ? "text-white" : "text-gray-700"}`}>{msg.message}</p>
                      <div className={`flex items-center gap-2 mt-1 ${isOutbound ? "justify-end" : ""}`}>
                        <span className={`text-[10px] ${isOutbound ? "text-blue-100" : "text-muted-foreground"}`}>
                          {formatTime(msg.timestamp)}
                        </span>
                        {isOutbound && msg.sent_by && (
                          <span className={`text-[10px] ${isOutbound ? "text-blue-100" : "text-muted-foreground"}`}>· {msg.sent_by}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        {/* Reply bar */}
        <div className="bg-white border-t px-4 py-3 rounded-b-lg shrink-0" data-testid="thread-reply-bar">
          <div className="max-w-3xl mx-auto">
            {(channel === "sms" || channel === "both") && (
              <p className="text-[10px] text-gray-400 mb-1.5">
                SMS recipients have consented. Opt-out footer auto-appended. <a href="/sms-terms" target="_blank" rel="noopener noreferrer" className="underline text-blue-500">SMS Terms</a>
              </p>
            )}
            <div className="flex items-end gap-2">
              <select
                value={channel}
                onChange={e => setChannel(e.target.value)}
                className="h-10 px-2 text-xs border rounded-md bg-white"
                data-testid="thread-channel"
              >
                <option value="sms">SMS Only</option>
                <option value="both">SMS + Email</option>
                <option value="email">Email Only</option>
              </select>
              <textarea
                placeholder={`Message ${threadLabel}'s parent...`}
                value={msgText}
                onChange={e => setMsgText(e.target.value)}
                onKeyDown={e => handleKey(e, [activeThread])}
                className="flex-1 min-h-[110px] max-h-[260px] text-base p-3 border rounded-md resize-y focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none leading-relaxed"
                rows={4}
                data-testid="thread-input"
              />
              <Button
                onClick={() => handleSend([activeThread])}
                disabled={sending || !msgText.trim()}
                className="h-10 bg-blue-600 hover:bg-blue-700"
                data-testid="thread-send"
              >
                {sending ? "..." : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════ INBOX VIEW ═══════════════════
  return (
    <div className="flex flex-col bg-white border rounded-lg" data-testid="threaded-inbox-list">
      {/* Header */}
      <div className="border-b px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} className="gap-1" data-testid="inbox-back">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs font-medium">Groups</span>
            </Button>
          )}
          <div className="min-w-0">
            <h1 className="text-base font-bold flex items-center gap-2 truncate">
              <MessageSquare className="w-5 h-5 text-blue-600 shrink-0" />
              <span className="truncate">{groupName || groupCode} Messages</span>
            </h1>
            <p className="text-xs text-muted-foreground">{campers.length} camper families</p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setActiveThread("compose-all")}
          className="text-xs"
          data-testid="inbox-message-all"
        >
          <Megaphone className="w-3.5 h-3.5 mr-1" />Message All
        </Button>
      </div>

      {/* Search + date picker */}
      <div className="border-b px-4 py-2 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by camper or parent name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="inbox-search"
          />
        </div>
        <div className="relative">
          <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            type="date"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            className="pl-9 pr-8 h-9 text-sm w-full sm:w-[170px]"
            data-testid="inbox-date-filter"
          />
          {dateFilter && (
            <button
              onClick={() => setDateFilter("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100"
              data-testid="inbox-date-clear"
              aria-label="Clear date"
            >
              <X className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {dateFilter && (
        <div className="bg-blue-50 border-b px-4 py-1.5 text-[11px] text-blue-800">
          Showing messages from <strong>{dateFilter}</strong> only. <button onClick={() => setDateFilter("")} className="underline">Clear filter</button>
        </div>
      )}

      {/* Compose-all bar */}
      {activeThread === "compose-all" && (
        <div className="bg-blue-50 border-b px-4 py-3" data-testid="inbox-compose-all">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-blue-600" />
              Message all {campers.length} families in {groupCode}
            </span>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setActiveThread(null)}>Cancel</Button>
          </div>
          <div className="flex items-end gap-2">
            <select
              value={channel}
              onChange={e => setChannel(e.target.value)}
              className="h-10 px-2 text-xs border rounded-md bg-white"
              data-testid="compose-all-channel"
            >
              <option value="sms">SMS Only</option>
              <option value="both">SMS + Email</option>
              <option value="email">Email Only</option>
            </select>
            <textarea
              placeholder={`Message all ${campers.length} families...`}
              value={msgText}
              onChange={e => setMsgText(e.target.value)}
              onKeyDown={e => handleKey(e, [])}
              className="flex-1 min-h-[110px] max-h-[240px] text-base p-3 border rounded-md resize-y leading-relaxed"
              rows={4}
              data-testid="compose-all-input"
            />
            <Button
              onClick={() => handleSend([])}
              disabled={sending || !msgText.trim()}
              className="h-10 bg-blue-600 hover:bg-blue-700"
              data-testid="compose-all-send"
            >
              {sending ? "..." : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}

      {/* Conversations */}
      <div
        className="overflow-y-auto overscroll-contain"
        style={{ maxHeight: "calc(100dvh - 280px)", minHeight: "300px", WebkitOverflowScrolling: "touch" }}
      >
        {loading ? (
          <div className="text-center py-10 text-muted-foreground text-sm">Loading...</div>
        ) : filteredConvos.length === 0 && filteredNewCampers.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium">{dateFilter ? `No messages on ${dateFilter}` : "No conversations yet"}</p>
            <p className="text-xs mt-1">Click a camper below to start a conversation.</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredConvos.map(convo => {
              const lastMsg = convo.lastMessage;
              const isInbound = lastMsg?.direction === "inbound";
              const preview = (lastMsg?.message || "").slice(0, 90);
              return (
                <button
                  key={convo.camper_id}
                  onClick={() => setActiveThread(convo.camper_id)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left"
                  data-testid={`convo-card-${convo.camper_id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center font-bold text-sm shrink-0">
                    {convo.camperName.split(" ").map(w => w[0]).join("").slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate" data-testid={`convo-name-${convo.camper_id}`}>{convo.camperName}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0">{formatTime(convo.lastTime)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-0.5 gap-2">
                      <p className="text-xs text-muted-foreground truncate">
                        {isInbound ? "" : "You: "}{preview}
                      </p>
                      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    </div>
                  </div>
                </button>
              );
            })}
            {/* Campers without any message yet — surface them so admin/sup can start a conversation */}
            {filteredNewCampers.length > 0 && !dateFilter && (
              <div className="px-4 py-2 bg-gray-50 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                No messages yet
              </div>
            )}
            {!dateFilter && filteredNewCampers.map(c => (
              <button
                key={c.id}
                onClick={() => setActiveThread(c.id)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left"
                data-testid={`convo-new-${c.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center font-bold text-sm shrink-0">
                  {(`${c.first_name || ""} ${c.last_name || ""}`).trim().split(" ").map(w => w[0]).join("").slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium truncate">
                    {c.first_name} {c.last_name}
                  </span>
                  <p className="text-xs text-muted-foreground truncate">Tap to send first message</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
